import React, { useState, useEffect, useContext } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Users, UserPlus } from "lucide-react";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { getUsers, deleteUserApi } from "../../services/userApi";
import { ThemeContext } from "../ThemeContext.jsx";
import AdminUsersTab from "../ui/AdminUsersTab.tsx";
import CreateUserTab from "../ui/CreateUserTab.tsx";
import { User, AlertConfig } from "../ui/types.ts";

interface UserManagementModalProps {
  onClose: () => void;
  isOpen: boolean;
}

interface StyledProps {
  $isDark?: boolean;
  $variant?: "primary" | "danger";
  disabled?: boolean;
}

const ModalContainer = styled(Box)<StyledProps>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => (props.$isDark ? "transparent" : "#FFFFFF")};
`;

const TabContainer = styled(Box)<StyledProps>`
  width: 100%;
  background-color: ${(props) => (props.$isDark ? "#18181B" : "#F3F4F6")};
  padding: 4px;
  border-radius: 8px;
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  position: relative;
`;

const TabSlider = styled(Box)<StyledProps & { $position: string }>`
  position: absolute;
  width: calc(50% - 6px);
  height: calc(100% - 8px);
  background-color: ${(props) => (props.$isDark ? "#22C55E" : "#16A34A")};
  border-radius: 6px;
  transition: transform 0.3s ease-in-out;
  transform: ${(props) =>
    props.$position === "left" ? "translateX(0)" : "translateX(100%)"};
  left: 4px;
  top: 4px;
`;

const Tab = styled(Box)<StyledProps & { selected?: boolean }>`
  flex: 1;
  padding: 8px;
  text-align: center;
  color: ${(props) =>
    props.selected
      ? props.$isDark
        ? "#18181B"
        : "#FFFFFF"
      : props.$isDark
      ? "#FFFFFF"
      : "#1F2937"};
  cursor: pointer;
  border-radius: 6px;
  position: relative;
  z-index: 1;
  transition: color 0.2s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: ${(props) => (props.selected ? 600 : 400)};

  &:hover {
    background-color: ${(props) =>
      !props.selected
        ? props.$isDark
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.1)"
        : "transparent"};
  }
`;

export default function UserManagementModal({
  onClose,
  isOpen,
}: UserManagementModalProps) {
  const { isDarkMode } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState<"admin" | "create">("admin");
  const [textConfig, setTextConfig] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [userData, setUserData] = useState<User | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  const [alertConfig, setAlertConfig] = useState<AlertConfig>({
    open: false,
    type: "success",
    message: "",
    title: "",
    onConfirm: undefined,
  });

  useEffect(() => {
    const fetchTextConfig = async () => {
      try {
        const response = await fetch("/text.json");
        const data = await response.json();
        setTextConfig(data);
      } catch (error) {
        console.error("Error loading text config:", error);
      }
    };
    fetchTextConfig();
  }, []);

  useEffect(() => {
    if (isOpen && activeTab === "admin" && !hasInitialized && users.length === 0 && !fetchError && !isLoading) {
      setHasInitialized(true);
      fetchUsers();
    }
  }, [isOpen, activeTab, hasInitialized]);

  const fetchUsers = async () => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      setFetchError(null);
      
      const usersData = await getUsers();
      
      const formattedUsers = Array.isArray(usersData)
        ? usersData.map((user) => ({
            ...user,
            groups: user.groups || [],
          }))
        : [];

      setUsers(formattedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
      setFetchError(error.message || "Error al obtener usuarios");
      
      if (error.message && !error.message.includes('500')) {
        setAlertConfig({
          open: true,
          type: "error",
          message: textConfig.USER_MANAGEMENT_MODAL_ERROR_FETCHING_USERS_MESSAGE || "Error al obtener la lista de usuarios",
          title: textConfig.USER_MANAGEMENT_MODAL_ERROR_TITLE || "Error",
          onConfirm: () => handleCloseAlert(),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (username: string) => {
    setAlertConfig({
      open: true,
      type: "confirm",
      message: (textConfig.USER_MANAGEMENT_MODAL_DELETE_CONFIRM || "¿Está seguro que desea eliminar al usuario {username}?").replace('{username}', username),
      title: textConfig.USER_MANAGEMENT_MODAL_DELETE_CONFIRM_TITLE || "Confirmar eliminación",
      onConfirm: async () => {
        try {
          setAlertConfig({
            open: true,
            type: "loading",
            message: (textConfig.USER_MANAGEMENT_MODAL_DELETING_USER || "Eliminando usuario {username}...").replace('{username}', username),
            title: textConfig.USER_MANAGEMENT_MODAL_DELETING_TITLE || "Eliminando usuario",
            onConfirm: undefined,
          });
          
          const result = await deleteUserApi(username);

          await fetchUsers();
          
          if (userData && userData.username === username) {
            setUserData(null);
          }

          setAlertConfig({
            open: true,
            type: "success",
            message: (textConfig.USER_MANAGEMENT_MODAL_DELETE_SUCCESS || "El usuario {username} ha sido eliminado correctamente").replace('{username}', username),
            title: textConfig.USER_MANAGEMENT_MODAL_SUCCESS_TITLE || "Usuario eliminado",
            onConfirm: () => handleCloseAlert(),
          });

        } catch (error) {
          console.error("Error deleting user:", error);
          
          setAlertConfig({
            open: true,
            type: "error",
            message: (textConfig.USER_MANAGEMENT_MODAL_DELETE_ERROR || "Error al eliminar usuario: {error}").replace('{error}', (error as Error).message || textConfig.USER_MANAGEMENT_MODAL_UNKNOWN_ERROR || "Error desconocido"),
            title: textConfig.USER_MANAGEMENT_MODAL_ERROR_TITLE || "Error",
            onConfirm: () => handleCloseAlert(),
          });
        }
      },
    });
  };

  const updateUserData = (user: User | null) => {
    setUserData(user);
  };

  const handleCloseAlert = () => {
    setAlertConfig((prev) => ({ ...prev, open: false }));
  };

  const handleUserCreated = async () => {
    setAlertConfig({
      open: true,
      type: "loading",
      message: textConfig.USER_MANAGEMENT_MODAL_REFRESHING || "Actualizando lista de usuarios...",
      title: textConfig.USER_MANAGEMENT_MODAL_LOADING_TITLE || "Cargando",
      onConfirm: undefined,
    });

    try {
      await fetchUsers();
      
      setAlertConfig({
        open: true,
        type: "success",
        message: textConfig.USER_MANAGEMENT_MODAL_USER_CREATED_SUCCESS || "Usuario creado exitosamente. La lista ha sido actualizada.",
        title: textConfig.USER_MANAGEMENT_MODAL_SUCCESS_TITLE || "Éxito",
        onConfirm: () => {
          handleCloseAlert();
          setActiveTab("admin");
        },
      });
    } catch (error) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig.USER_MANAGEMENT_MODAL_REFRESH_ERROR || "Usuario creado, pero hubo un error al actualizar la lista. Recargue la página.",
        title: textConfig.USER_MANAGEMENT_MODAL_ERROR_TITLE || "Error",
        onConfirm: () => {
          handleCloseAlert();
          setActiveTab("admin");
        },
      });
    }
  };

  const handleTabChange = (tab: "admin" | "create") => {
    setActiveTab(tab);
    
    if (tab === "admin" && users.length === 0 && !fetchError && !isLoading) {
      fetchUsers();
    }
  };

  return (
    <BaseModal
      title={textConfig.USER_MANAGEMENT_TEXT_TITLE || "Gestión de Usuarios"}
      onClose={onClose}
      isOpen={isOpen}
    >
      <ModalContainer $isDark={isDarkMode}>
        <TabContainer $isDark={isDarkMode}>
          <TabSlider
            $position={activeTab === "admin" ? "left" : "right"}
            $isDark={isDarkMode}
          />
          <Tab
            selected={activeTab === "admin"}
            onClick={() => handleTabChange("admin")}
            $isDark={isDarkMode}
          >
            <Users size={18} />
            {textConfig.USER_MENU_TEXT_ADMIN || "Administrar"}
          </Tab>
          <Tab
            selected={activeTab === "create"}
            onClick={() => handleTabChange("create")}
            $isDark={isDarkMode}
          >
            <UserPlus size={18} />
            {textConfig.USER_MENU_TEXT_USER || "Crear"}
          </Tab>
        </TabContainer>

        {activeTab === "admin" && (
          <AdminUsersTab
            users={users}
            userData={userData}
            updateUserData={updateUserData}
            handleDeleteUser={handleDeleteUser}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            isDarkMode={isDarkMode}
            textConfig={textConfig}
            setAlertConfig={setAlertConfig}
            fetchUsers={fetchUsers}
            fetchError={fetchError}
          />
        )}

        {activeTab === "create" && (
          <CreateUserTab
            isDarkMode={isDarkMode}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            textConfig={textConfig}
            setAlertConfig={setAlertConfig}
            fetchUsers={fetchUsers}
            users={users}
            onUserCreated={handleUserCreated}
          />
        )}

        <CustomAlert
          open={alertConfig.open}
          type={alertConfig.type}
          message={alertConfig.message}
          title={alertConfig.title}
          onClose={handleCloseAlert}
          onConfirm={alertConfig.onConfirm}
          $isDark={isDarkMode}
          textConfig={textConfig}
        />
      </ModalContainer>
    </BaseModal>
  );
}